'use client';

import {
  type TraceToolCall,
  isRenderableToolName,
  type TracePhase,
  usesReadToolCard,
  usesStandardToolCard,
} from './types';
import { getToolDisplayName, normalizeToolName } from './utils/tool-config';
import { isBrowserToolName } from '@terra/shared-chat-trace/trace-view/utils/tool-config.helpers';
import { getToolCategory } from './utils/tool-categories';
import { CodeExecutionToolCard } from './components/CodeExecutionToolCard';
import { PythonArtifact } from '../../artifacts/python/PythonArtifact';
import { CliArtifact } from '../../artifacts/cli/CliArtifact';
import { LogArtifact } from '../../artifacts/log/LogArtifact';
import { FileToolArtifact } from '../../artifacts/file-tool/FileToolArtifact';
import { TraceReadToolCard } from './components/TraceReadToolCard';
import { AuthenticateToolCard } from '../components/tools/authenticate/AuthenticateToolCard';
import { StandardToolCard } from './components/StandardToolCard';
import { InlineToolCard } from './components/InlineToolCard';
import { SelectEvidenceToolCard } from './components/SelectEvidenceToolCard';
import { PlanUpdateCard } from './components/PlanUpdateCard';
import { WriteFileToolCard } from './components/WriteFileToolCard';
import { OobDetectionToolCard } from './components/OobDetectionToolCard';
import { TraceAskUserToolCard } from './components/TraceAskUserToolCard';
import { EmailInboxToolCard } from './components/EmailInboxToolCard';
import type { SseSubscribe } from '@terra/shared-sse-contracts';

type TraceToolCardProps = {
  toolCall: TraceToolCall;
  tracePhase?: TracePhase;
  preferHarOverToolOutput?: boolean;
  onHideMessage?: () => void;
  onSSESubscribe?: SseSubscribe;
};

export function TraceToolCard({
  toolCall,
  tracePhase = 'attack',
  preferHarOverToolOutput,
  onHideMessage,
  onSSESubscribe,
}: TraceToolCardProps) {
  const normalizedName = normalizeToolName(toolCall.name);

  if (!isRenderableToolName(toolCall.name, tracePhase)) {
    return null;
  }

  if (normalizedName === 'write-file') {
    return <WriteFileToolCard toolCall={toolCall} />;
  }

  if (normalizedName === 'todo-write') {
    const isMerge = toolCall.args?.merge === true;

    if (isMerge) {
      return <PlanUpdateCard toolCall={toolCall} />;
    }

    return null;
  }

  const displayName = getToolDisplayName(
    toolCall.name,
    toolCall.args,
    tracePhase,
  );
  const category = getToolCategory(toolCall.name);

  if (normalizedName === 'authenticate') {
    return (
      <AuthenticateToolCard
        input={toolCall.args}
        output={toolCall.output}
        status={toolCall.status}
        error={toolCall.error}
        durationMs={
          typeof toolCall.metadata?.durationMs === 'number'
            ? toolCall.metadata.durationMs
            : toolCall.timing?.durationMs
        }
      />
    );
  }

  if (
    normalizedName === 'add-evidence' ||
    normalizedName === 'add-http-evidence'
  ) {
    return <SelectEvidenceToolCard toolCall={toolCall} />;
  }

  if (normalizedName === 'out-of-band-detection') {
    return (
      <OobDetectionToolCard toolCall={toolCall} displayName={displayName} />
    );
  }

  if (normalizedName === 'ask-user') {
    return <TraceAskUserToolCard toolCall={toolCall} />;
  }

  if (normalizedName === 'save-relevant-emails') {
    return <EmailInboxToolCard toolCall={toolCall} displayName={displayName} />;
  }

  if (
    normalizedName === 'file-upload-client' ||
    normalizedName === 'prepare-file'
  ) {
    return <FileToolArtifact toolCall={toolCall} displayName={displayName} />;
  }

  if (usesReadToolCard(toolCall.name, tracePhase)) {
    return (
      <TraceReadToolCard
        toolCall={toolCall}
        tracePhase={tracePhase}
        onHideMessage={onHideMessage}
      />
    );
  }

  if (usesStandardToolCard(toolCall.name, tracePhase)) {
    return (
      <StandardToolCard
        toolCall={toolCall}
        displayName={displayName}
        tracePhase={tracePhase}
        preferHarOverToolOutput={preferHarOverToolOutput}
        onHideMessage={onHideMessage}
        onSSESubscribe={onSSESubscribe}
      />
    );
  }

  if (category === 'code-execution') {
    if (
      normalizedName === 'coder' ||
      normalizedName === 'coder-tool' ||
      normalizedName === 'python' ||
      normalizedName === 'python-tool' ||
      normalizedName === 'sub-agent-coder'
    ) {
      return (
        <PythonArtifact
          toolCall={toolCall}
          displayName={displayName}
          tracePhase={tracePhase}
          onHideMessage={onHideMessage}
        />
      );
    }

    if (normalizedName === 'cli-tool') {
      return <CliArtifact toolCall={toolCall} displayName={displayName} />;
    }

    if (normalizedName === 'remote-c2-command') {
      return <CliArtifact toolCall={toolCall} displayName={displayName} />;
    }

    if (normalizedName === 'log-tool') {
      return <LogArtifact toolCall={toolCall} displayName={displayName} />;
    }

    return (
      <CodeExecutionToolCard toolCall={toolCall} displayName={displayName} />
    );
  }

  if (category === 'intelligence') {
    return (
      <InlineToolCard
        toolCall={toolCall}
        displayName={displayName}
        tracePhase={tracePhase}
        onHideMessage={onHideMessage}
      />
    );
  }

  if (isBrowserToolName(toolCall.name) || normalizedName === 'load-tools') {
    return (
      <InlineToolCard
        toolCall={toolCall}
        displayName={displayName}
        tracePhase={tracePhase}
        onHideMessage={onHideMessage}
      />
    );
  }

  return (
    <StandardToolCard
      toolCall={toolCall}
      displayName={displayName}
      isEndTestTool={normalizedName === 'end-test'}
      tracePhase={tracePhase}
      preferHarOverToolOutput={preferHarOverToolOutput}
      onHideMessage={onHideMessage}
      onSSESubscribe={onSSESubscribe}
    />
  );
}
